import {observer} from 'mobx-react';
import React from 'react';
import {PromiseMessage} from '../../../../../lib/components';
import {ReactNodeGenerator} from '../../../../../lib/helpers';
import {FlashcardsEditStore, FlashcardSideStore} from './FlashcardsEditStore';

function* cardSideEditorRenderFn(store: FlashcardSideStore): ReactNodeGenerator {
  const {text} = store;

  yield (
    <div className="form-group" key="text">
      <div className="label">Text</div>
      <textarea className="form-control"
                value={text.value}
                onChange={text.onChangeHandler}/>
    </div>
  );

  const {imageUrl, imageFile, imageUpload} = store;

  if (imageUrl === null) {
    yield (
      <div className="row no-gutters" key="image-1">
        <div className="col">
          <div className="form-group">
            <label>Image</label>
            <input type="file" className="form-control-file"
                   onChange={store.handleImageFileChange}/>
          </div>
        </div>
        <div className="col-auto">
          <button type="button" className="btn btn-primary"
                  disabled={imageFile === null || imageUpload?.isPending === true}
                  onClick={store.uploadImage}>
            Upload
          </button>
        </div>
      </div>
    );
    yield (
      <PromiseMessage key="image-2"
                      label="Uploading" state={imageUpload} align="right"/>
    );
  }

  if (imageUrl !== null) {
    yield (
      <div className="row" key="image-3">
        <div className="col flashcard-image-container">
          <img className="flashcard-image" src={imageUrl} alt="Image not found"/>
        </div>
      </div>
    );

    yield (
      <div className="row justify-content-end" key="image-4">
        <div className="col-auto">
          <button type="button" className="btn btn-primary"
                  onClick={store.clearImage}>
            Clear
          </button>
        </div>
      </div>
    );
  }

  const {audioUrl, audioFile, audioUpload, audioElementRef} = store;

  if (audioUrl === null) {
    yield (
      <div className="row no-gutters" key="audio-1">
        <div className="col">
          <div className="form-group">
            <label>Audio</label>
            <input type="file" className="form-control-file"
                   accept="audio/mpeg,.mp3,audio/wav,.wav,audio/ogg,.ogg"
                   onChange={store.handleAudioFileChange}/>
          </div>
        </div>
        <div className="col-auto">
          <button type="button" className="btn btn-primary"
                  disabled={audioFile === null || audioUpload?.isPending === true}
                  onClick={store.uploadAudio}>
            Upload
          </button>
        </div>
      </div>
    );

    yield (
      <PromiseMessage key="audio-2"
                      label="Uploading" state={audioUpload} align="right"/>
    );
  }

  if (audioUrl !== null) {
    yield (
      <div className="row justify-content-end" key="audio-3">
        <div className="col-auto">
          <audio ref={audioElementRef} src={audioUrl} preload="auto"/>
          <button type="button" className="btn btn-primary"
                  onClick={store.playAudio}>
            Play
          </button>
          <button type="button" className="btn btn-primary"
                  onClick={store.clearAudio}>
            Clear
          </button>
        </div>
      </div>
    );
  }

  if (imageUrl === null && text.value.trim() === '')
    yield (
      <div className="row" key="errors">
        <div className="col">
          <div className="alert alert-danger">
            Text or Image must be specified
          </div>
        </div>
      </div>);
}

@observer
class FlashcardSideEditor extends React.Component<{ store: FlashcardSideStore }> {
  render() {
    return Array.from(cardSideEditorRenderFn(this.props.store));
  }
}

@observer
class FlashcardEditor extends React.Component<{ store: FlashcardsEditStore, index: number }> {
  render() {
    const {store, index} = this.props;

    const card = store.cards[index];
    const front = card.front;
    const back = card.back;

    return (
      <div className="row">
        <div className="col">
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col">
                  <div className="card flashcard-side">
                    <div className="card-header">
                      Front side
                    </div>
                    <div className="card-body">
                      <FlashcardSideEditor store={front}/>
                    </div>
                  </div>
                </div>
                <div className="col">
                  <div className="card flashcard-side">
                    <div className="card-header">
                      Back side
                    </div>
                    <div className="card-body">
                      <FlashcardSideEditor store={back}/>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row justify-content-end">
                <div className="col-auto">
                  <button type="button" className="btn btn-warning"
                          onClick={() => store.removeCard(index)}>Remove
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>);
  }
}

export function* flashcardsEditStoreRenderer(store: FlashcardsEditStore): ReactNodeGenerator {
  for (let index = 0; index < store.cards.length; index++)
    yield(<FlashcardEditor store={store} index={index} key={index}/>);

  yield (
    <div className="row justify-content-end" key="add-card">
      <div className="col-auto">
        <button type="button" className="btn btn-primary"
                onClick={store.addCard}>
          Add card
        </button>
      </div>
    </div>);


  if (store.cards.length === 0)
    yield (
      <div className="row" key="error-no-cards">
        <div className="col text-center">
          <div className="alert alert-danger">
            No cards
          </div>
        </div>
      </div>);
}
